import React, { Component, createRef } from "react";
import { Input, Button, Result, message } from "antd";
import { SmileOutlined } from "@ant-design/icons";
import { Smartphone } from "lucide-react";
import { motion } from "framer-motion";
import { connect } from "react-redux";
import {
  requestServerPost,
  selectPage,
  requestNodeServerPost,
} from "../../actions";
import PopupManager from "../../managers/PopupManager";
import DataHelper from "../../helpers/DataHelper";
import { PRO_TRANSLATIONS } from "../../global/ProLanguageContext";

class PairScreenPage extends Component {
  constructor(props) {
    super(props);
    this.state = { currentStep: 0, pairingcode: "" };
    this.inputRef = createRef();
  }

  componentDidMount() {
    setTimeout(() => {
      if (this.inputRef.current) this.inputRef.current.focus();
    }, 100);
  }

  next() {
    this.setState({ currentStep: this.state.currentStep + 1 });
  }

  updateScreen = (pairingcode) => {
    const {
      requestServerPost,
      data,
      selectedBrandData,
      selectedBranchData,
      usertoken,
    } = this.props;
    const params = new FormData();
    params.append("brandid", selectedBrandData.BrandID);
    params.append("branchid", selectedBranchData.BranchID);
    params.append("screenname", data.ScreenName);
    params.append("screenid", data.ScreenID);
    params.append("pairingcode", pairingcode);
    requestServerPost(
      "update_screen",
      params,
      {},
      () => {
        if (this.props.onSuccess) this.props.onSuccess();
        this.next();
      },
      usertoken,
    );
  };

  pairScreen = (
    screenname,
    pairingcode,
    screensettings,
    callback = () => {},
  ) => {
    const { requestNodeServerPost, usertoken, lang } = this.props;
    const t = (PRO_TRANSLATIONS[lang] || PRO_TRANSLATIONS.en).screen;
    const params = new URLSearchParams();
    params.append("account", screensettings.AccountCode);
    params.append("brand", screensettings.BrandCode);
    params.append("branch", screensettings.BranchCode);
    params.append("screen", screensettings.ScreenCode);
    params.append("screenname", screenname);
    params.append("code", pairingcode);
    DataHelper.clearScreenUnpariRequest(screensettings);
    setTimeout(() => {
      requestNodeServerPost(
        "attemptscreenpairing",
        params,
        { "Content-Type": "application/x-www-form-urlencoded" },
        (response) => {
          if (response.data.status === "success") callback();
          else message.error(t.pairErrNotAvailable);
        },
        usertoken,
      );
    }, 500);
  };

  checkScreenAvailable = (pairingcode, callback = () => {}) => {
    const { requestNodeServerPost, usertoken, lang } = this.props;
    const t = (PRO_TRANSLATIONS[lang] || PRO_TRANSLATIONS.en).screen;
    const params = new URLSearchParams();
    if (pairingcode.length !== 6) {
      message.error(t.pairErrCodeLength);
      return;
    }
    params.append("code", pairingcode);
    requestNodeServerPost(
      "checkscreenpairing",
      params,
      { "Content-Type": "application/x-www-form-urlencoded" },
      (response) => {
        if ("result" in response.data && response.data.result === 1) {
          this.setState({ pairingcode }, callback);
        } else {
          message.error(t.pairErrNoScreen);
        }
      },
      usertoken,
    );
  };

  handlePair = () => {
    const { pairingcode } = this.state;
    const { data } = this.props;
    this.checkScreenAvailable(pairingcode, () => {
      this.pairScreen(data.ScreenName, pairingcode, data, () => {
        this.updateScreen(pairingcode);
      });
    });
  };

  render() {
    const { currentStep, pairingcode } = this.state;
    const { lang } = this.props;
    const t = (PRO_TRANSLATIONS[lang] || PRO_TRANSLATIONS.en).screen;
    const isReady = pairingcode.length === 6;

    const bleed = { margin: -24, overflow: "hidden" };
    const body = { maxWidth: 440, margin: "0 auto", padding: "32px 40px" };

    const stepDots = (active) => (
      <div style={{ display: "flex", justifyContent: "center", gap: 6, marginBottom: 20 }}>
        {[0, 1].map((i) => (
          <div key={i} style={{
            width: i === active ? 20 : 8, height: 8, borderRadius: 4,
            background: i === active ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.35)",
            transition: "all 0.3s",
          }} />
        ))}
      </div>
    );

    if (currentStep === 1) {
      return (
        <div style={{ ...bleed }}>
          <Result
            icon={<SmileOutlined style={{ color: "#6366f1" }} />}
            title={t.pairSuccess}
            subTitle={t.pairSuccessDesc}
            extra={
              <button
                onClick={PopupManager.closePopup}
                style={{
                  padding: "10px 28px", borderRadius: 10, border: "none",
                  background: "linear-gradient(135deg,#6366f1,#4f46e5)",
                  color: "#fff", fontWeight: 600, fontSize: 14, cursor: "pointer",
                  boxShadow: "0 2px 8px rgba(99,102,241,0.32)",
                }}
              >
                {t.pairDone}
              </button>
            }
          />
        </div>
      );
    }

    return (
      <div style={{ ...bleed }}>
        {/* ── Gradient header ── */}
        <div style={{
          background: "linear-gradient(135deg,#6366f1 0%,#4f46e5 100%)",
          padding: "36px 40px", textAlign: "center", color: "#fff",
        }}>
          {stepDots(0)}
          <motion.div
            animate={{ y: [0, -8, 0] }}
            transition={{ repeat: Infinity, duration: 3, ease: "easeInOut" }}
          >
            <Smartphone size={56} color="#ffffff" style={{ marginBottom: 14, opacity: 0.9 }} />
          </motion.div>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#fff", marginBottom: 6 }}>
            {t.pairTitle}
          </div>
          <div style={{ fontSize: 14, color: "rgba(255,255,255,0.75)" }}>
            {t.pairSubtitle}
          </div>
        </div>

        {/* ── Code input + button ── */}
        <div style={{ ...body, textAlign: "center" }}>
          <div style={{
            position: "relative", display: "flex",
            justifyContent: "center", gap: 8, marginBottom: 28,
          }}>
            <Input
              ref={this.inputRef}
              autoFocus
              value={pairingcode}
              onChange={(e) =>
                this.setState({
                  pairingcode: e.target.value.replace(/[^A-Za-z0-9]/g, "").slice(0, 6),
                })
              }
              onPressEnter={isReady ? this.handlePair : undefined}
              maxLength={6}
              style={{
                position: "absolute", opacity: 0,
                width: "100%", height: "100%",
                cursor: "text", zIndex: 10, top: 0, left: 0,
              }}
            />
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                style={{
                  width: 44, height: 54, borderRadius: 12,
                  border: `2px solid ${pairingcode.length === i ? "#6366f1" : pairingcode.length > i ? "#10b981" : "#e2e8f0"}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 22, fontWeight: 700, color: "#0f172a",
                  background: pairingcode.length === i ? "#f5f3ff" : "#ffffff",
                  transition: "all 0.2s ease",
                  boxShadow: pairingcode.length === i ? "0 0 0 4px rgba(99,102,241,0.1)" : "none",
                  flexShrink: 0,
                }}
              >
                {pairingcode[i] || ""}
              </div>
            ))}
          </div>

          <button
            disabled={!isReady}
            onClick={this.handlePair}
            style={{
              width: "100%", height: 48, borderRadius: 10, border: "none",
              background: isReady ? "linear-gradient(135deg,#10b981,#059669)" : "#e2e8f0",
              color: isReady ? "#fff" : "#94a3b8",
              fontSize: 15, fontWeight: 600,
              cursor: isReady ? "pointer" : "not-allowed",
              transition: "all 0.2s",
              boxShadow: isReady ? "0 2px 10px rgba(16,185,129,0.3)" : "none",
            }}
          >
            {isReady ? t.pairConnect : t.pairEnterCode}
          </button>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  userdata: state.userdata,
  usertoken: state.usertoken,
  selectedBrandData: state.selectedBrandData,
  selectedBranchData: state.selectedBranchData,
});

export default connect(mapStateToProps, {
  requestServerPost,
  selectPage,
  requestNodeServerPost,
})(PairScreenPage);
